import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './stores/authStore';
import useNotificationStore from './stores/notificationStore';
import useChannelStore from './stores/channelStore';
import useSocket from './hooks/useSocket';
import { getSocket } from './socket/client';

import { lazy, Suspense, useEffect, useRef } from 'react';

const SuperAdminLogin = lazy(() => import('./components/auth/SuperAdminLogin'));
const SuperAdminDashboard = lazy(() => import('./components/superadmin/SuperAdminDashboard'));
const TenantLogin = lazy(() => import('./components/auth/LoginForm'));
const ForgotPassword = lazy(() => import('./components/auth/RequestPasswordReset'));
const ResetPassword = lazy(() => import('./components/auth/PasswordReset'));
const SetPassword = lazy(() => import('./components/auth/SetPassword'));
const AppShell = lazy(() => import('./components/layout/AppShell'));

/* ---------- Loading spinner ---------- */
function LoadingScreen() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-950">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
        <span className="text-sm text-gray-400">Loading HelixChat...</span>
      </div>
    </div>
  );
}

/* ---------- Protected Route wrapper ---------- */
function ProtectedRoute({ requireSuperAdmin = false }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const tenantSlug = useAuthStore((s) => s.tenantSlug);

  if (!isAuthenticated) {
    if (requireSuperAdmin) {
      return <Navigate to="/superadmin/login" replace />;
    }
    return <Navigate to={tenantSlug ? `/t/${tenantSlug}/login` : '/'} replace />;
  }

  if (requireSuperAdmin && user?.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/* ---------- Socket initializer (only mounted when authed) ---------- */
function SocketProvider({ children }) {
  useSocket();
  return children;
}

/* ---------- Authenticated app shell wrapper ---------- */
function AuthenticatedApp() {
  useSocket();
  const currentUser = useAuthStore((s) => s.user);
  const activeChannelId = useChannelStore((s) => s.activeChannelId);

  // Notification permission is requested via user gesture in the UI
  // (browsers block programmatic requestPermission without user interaction)

  // Global socket listener for notifications + unread badges
  // Uses refs to avoid stale closures and re-attaches on reconnect
  const currentUserIdRef = useRef(currentUser?.id);
  const activeChannelIdRef = useRef(activeChannelId);
  currentUserIdRef.current = currentUser?.id;
  activeChannelIdRef.current = activeChannelId;

  useEffect(() => {
    let retryTimer;

    function handler(data) {
      if (data.user_id === currentUserIdRef.current) return;

      // Increment unread badge if not viewing that channel
      const msgChannel = data.channel_id || data.dm_conversation_id;
      if (msgChannel && msgChannel !== activeChannelIdRef.current) {
        useNotificationStore.getState().incrementUnread(msgChannel);
      }

      // Browser notification when not viewing that channel/DM
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // Only show notification if user is not currently viewing this channel/DM
        // or if the tab doesn't have focus
        const isViewingThis = msgChannel === activeChannelIdRef.current;
        const tabFocused = document.hasFocus();

        if (!isViewingThis || !tabFocused) {
          const sender = data.display_name || data.username || 'Someone';
          const preview = data.message_type === 'voice'
            ? '🎤 Voice message'
            : (data.content || '').substring(0, 100);
          const channelLabel = data.channel_name ? `#${data.channel_name}` : 'DM';
          new Notification(`${sender} in ${channelLabel}`, {
            body: preview,
            icon: '/HelixChat.png',
            tag: data.id,
          });
        }
      }
    }

    function attach() {
      const socket = getSocket();
      if (!socket) return false;

      // Remove old listeners first to avoid duplicates
      socket.off('message:new', handler);
      socket.off('dm:new', handler);
      socket.on('message:new', handler);
      socket.on('dm:new', handler);

      // Re-attach on reconnect
      socket.off('connect', attach); // prevent duplicates
      socket.on('connect', attach);

      return true;
    }

    if (!attach()) {
      retryTimer = setInterval(() => {
        if (attach()) clearInterval(retryTimer);
      }, 500);
    }

    return () => {
      const socket = getSocket();
      if (socket) {
        socket.off('message:new', handler);
        socket.off('dm:new', handler);
        socket.off('connect', attach);
      }
      if (retryTimer) clearInterval(retryTimer);
    };
  }, []);

  return (
    <Suspense fallback={<LoadingScreen />}>
      <AppShell />
    </Suspense>
  );
}

/* ---------- Root redirect ---------- */
function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const tenantSlug = useAuthStore((s) => s.tenantSlug);

  if (isAuthenticated) {
    // Superadmin goes to superadmin dashboard
    if (user?.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    }
    if (tenantSlug && tenantSlug !== 'system') {
      return <Navigate to={`/t/${tenantSlug}`} replace />;
    }
  }

  if (tenantSlug && tenantSlug !== 'system') {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  // No tenant context — show a landing page, not superadmin login
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
      <div className="w-full max-w-md text-center">
        <img src="/HelixChat.png" alt="HelixChat" className="mx-auto mb-6 h-16 object-contain" />
        <h1 className="mb-2 text-2xl font-bold text-white">Welcome to HelixChat</h1>
        <p className="mb-6 text-gray-400">
          To access your workspace, use the link provided by your administrator:
        </p>
        <div className="mb-6 rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3">
          <p className="font-mono text-sm text-indigo-400">
            https://helixchat.ca/t/<span className="text-gray-500">your-workspace</span>/login
          </p>
        </div>
        <p className="text-sm text-gray-500">
          Are you an administrator?{' '}
          <a href="/superadmin/login" className="text-indigo-400 hover:text-indigo-300 underline">
            Super Admin Login
          </a>
        </p>
      </div>
    </div>
  );
}

/* ---------- App ---------- */
export default function App() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#f3f4f6',
            border: '1px solid #374151',
          },
          success: {
            iconTheme: { primary: '#6366f1', secondary: '#f3f4f6' },
          },
          error: {
            iconTheme: { primary: '#ef4444', secondary: '#f3f4f6' },
          },
        }}
      />

      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Root */}
          <Route path="/" element={<RootRedirect />} />

          {/* Super-admin */}
          <Route path="/superadmin/login" element={<SuperAdminLogin />} />
          <Route element={<ProtectedRoute requireSuperAdmin />}>
            <Route path="/superadmin/*" element={<SuperAdminDashboard />} />
          </Route>

          {/* Tenant: public auth routes */}
          <Route path="/t/:slug/login" element={<TenantLogin />} />
          <Route path="/t/:slug/forgot-password" element={<ForgotPassword />} />
          <Route path="/t/:slug/reset-password" element={<ResetPassword />} />
          <Route path="/t/:slug/set-password" element={<SetPassword />} />

          {/* Tenant: protected app */}
          <Route element={<ProtectedRoute />}>
            <Route path="/t/:slug/*" element={<AuthenticatedApp />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
